/**
 * ============================================================
 * KEJJA OS — src/lib/notificationsApi.ts
 * ============================================================
 */

import { api } from '@/lib/api';

export type NotificationPriority = 'critical' | 'normal' | 'quiet';
export type NotificationState = 'pending' | 'delivered' | 'read' | 'dismissed';
export type DigestMode = 'immediate' | 'hourly' | 'daily' | 'off';

export interface Notification {
  id: string;
  homeId: string | null;
  userId: string;
  module: string;
  eventType: string;
  priority: NotificationPriority;
  title: string;
  body: string | null;
  icon: string | null;
  actionUrl: string | null;
  actionLabel: string | null;
  entityType: string | null;
  entityId: string | null;
  metadata: Record<string, unknown>;
  state: NotificationState;
  channelsSent: string[];
  readAt: string | null;
  dismissedAt: string | null;
  createdAt: string;
}

export interface NotificationPreference {
  id: string;
  userId: string;
  eventType: string;
  inAppEnabled: boolean;
  emailEnabled: boolean;
  pushEnabled: boolean;
  soundEnabled: boolean;
  digestMode: DigestMode;
  quietHoursStart: string | null;
  quietHoursEnd: string | null;
}

export interface EventTypeMeta {
  key: string;
  module: string;
  label: string;
  description: string;
  defaultPriority: NotificationPriority;
}

export interface UpsertPrefPayload {
  eventType: string;
  inAppEnabled?: boolean;
  emailEnabled?: boolean;
  pushEnabled?: boolean;
  soundEnabled?: boolean;
  digestMode?: DigestMode;
  quietHoursStart?: string | null;
  quietHoursEnd?: string | null;
}

export type ListFilter = 'all' | 'unread' | 'read' | 'dismissed';

export const notificationsApi = {
  list: (filter: ListFilter = 'all', limit = 100) =>
    api.get<{ notifications: Notification[]; unreadCount: number }>(
      `/notifications?filter=${filter}&limit=${limit}`,
    ),

  unreadCount: () => api.get<{ count: number }>(`/notifications/unread-count`),

  markRead: (id: string) =>
    api.post<{ notification: Notification }>(`/notifications/${id}/read`, {}),
  markAllRead: () => api.post<{ markedCount: number }>(`/notifications/read-all`, {}),
  dismiss: (id: string) =>
    api.post<{ notification: Notification }>(`/notifications/${id}/dismiss`, {}),
  dismissAll: () => api.post<{ dismissedCount: number }>(`/notifications/dismiss-all`, {}),

  getPreferences: () =>
    api.get<{ preferences: NotificationPreference[]; catalog: EventTypeMeta[] }>(`/notifications/preferences`),
  upsertPreference: (body: UpsertPrefPayload) =>
    api.put<{ preference: NotificationPreference }>(`/notifications/preferences`, body),

  sendTest: () => api.post<{ sent: number }>(`/notifications/test`, {}),

  /** Returns URL for the SSE stream (add auth cookies via browser). */
  streamUrl: () => `/api/v1/notifications/stream`,
};
